import { getTpmsTireColor, TireStatus } from 'utils/tpms';
import TireTooltip from 'components/Tire/TireTooltip';
import BatteryIndicator from 'components/Tire/BatteryIndicator';

interface TireAlert {
  icon: string;
  color: string;
}

const TerrainFourWheels = ({
  values,
  tireData,
  batteryLow,
  width = 65,
  height = 119,
  tireStatusMap,
  tireStaleMap,
  tireAlertMap,
}: {
  values: any;
  tireData?: any;
  batteryLow?: { p1?: boolean; p2?: boolean; p3?: boolean; p4?: boolean };
  width?: number | string;
  height?: number | string;
  tireStatusMap?: {
    p1?: TireStatus;
    p2?: TireStatus;
    p3?: TireStatus;
    p4?: TireStatus;
  };
  tireStaleMap?: Record<string, boolean>;
  tireAlertMap?: Record<string, TireAlert>;
}) => {
  const {
    p1Status,
    p2Status,
    p3Status,
    p4Status,
    p1HasSensor,
    p2HasSensor,
    p3HasSensor,
    p4HasSensor,
    isDataStale,
    p1IsStale,
    p2IsStale,
    p3IsStale,
    p4IsStale,
  } = values;

  const isNumber = (value: any) => typeof value === 'number';

  const renderTireAlert = (key: string, x: number, y: number) => {
    const alert = tireAlertMap?.[key];
    if (!alert) return null;

    // Shrink play symbols (leakage) so they fit inside the tire circle
    const isPlayIcon = alert.icon.includes('▶');
    const alertFontSize = isPlayIcon ? (alert.icon.length > 1 ? 5 : 7) : 12;

    return (
      <g style={{ pointerEvents: 'none' }}>
        <circle
          cx={x}
          cy={y}
          r="6"
          fill={alert.color}
          stroke="#FFFFFF"
          strokeWidth="1.4"
        />
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#FFFFFF"
          fontSize={alertFontSize}
          fontWeight="900"
        >
          {alert.icon}
        </text>
      </g>
    );
  };

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 65 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line
        x1="5"
        y1="65.5"
        x2="60"
        y2="65.5"
        stroke="#4A5C72"
        strokeWidth="5"
      />

      <TireTooltip tireInfo={tireData?.p3}>
        <g>
          <rect
            x="5"
            y="50"
            width="10"
            height="30"
            rx="1.90055"
            fill={getTpmsTireColor(
              tireStatusMap?.p3 ?? p3Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p3 ?? p3IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={10}
            cy={65}
            tireBottomY={80}
            tireWidth={10}
            show={batteryLow?.p3}
          />
          {renderTireAlert('p3', 10, 65)}
        </g>
      </TireTooltip>

      <TireTooltip tireInfo={tireData?.p4}>
        <g>
          <rect
            x="50"
            y="50"
            width="10"
            height="30"
            rx="1.90055"
            fill={getTpmsTireColor(
              tireStatusMap?.p4 ?? p4Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p4 ?? p4IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={55}
            cy={65}
            tireBottomY={80}
            tireWidth={10}
            show={batteryLow?.p4}
          />
          {renderTireAlert('p4', 55, 65)}
        </g>
      </TireTooltip>

      <TireTooltip tireInfo={tireData?.p1}>
        <g>
          <rect
            x="5"
            y="0"
            width="10"
            height="30"
            rx="1.90055"
            fill={getTpmsTireColor(
              tireStatusMap?.p1 ?? p1Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p1 ?? p1IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={10}
            cy={15}
            tireBottomY={30}
            tireWidth={10}
            show={batteryLow?.p1}
          />
          {renderTireAlert('p1', 10, 15)}
        </g>
      </TireTooltip>

      <TireTooltip tireInfo={tireData?.p2}>
        <g>
          <rect
            x="50"
            y="0"
            width="10"
            height="30"
            rx="1.90055"
            fill={getTpmsTireColor(
              tireStatusMap?.p2 ?? p2Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p2 ?? p2IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={55}
            cy={15}
            tireBottomY={30}
            tireWidth={10}
            show={batteryLow?.p2}
          />
          {renderTireAlert('p2', 55, 15)}
        </g>
      </TireTooltip>

      <line
        x1="15"
        y1="12.5"
        x2="50"
        y2="12.5"
        stroke="#4A5C72"
        strokeWidth="5"
      />
      <line
        x1="32.5"
        y1="15"
        x2="32.5"
        y2="65"
        stroke="#4A5C72"
        strokeWidth="5"
      />
    </svg>
  );
};

export default TerrainFourWheels;
